import { Component, EventEmitter, inject, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import {
  ApiService,
  Eligibility,
  EligibilityBenefits,
  Patient,
  PatientInsuranceType,
  ShopperEstimate,
  ShopperEstimateInsurance,
  ShopperEstimateService,
} from '@mmx/shared'
import moment from 'moment-timezone'
import { Subject } from 'rxjs'
import { filter, finalize, map, switchMap, take, takeUntil, tap } from 'rxjs/operators'

import {
  PullEligibilityDialogComponent,
  PullEligibilityDialogInput,
} from '../../pull-eligibility-dialog'

export interface ShopperInsuranceChangeEvent {
  insurances: ShopperEstimate['insurances']
  eligibilityId?: string
  inputs: {
    deductible?: number
    coinsurance?: number
    copayment?: number
    outOfPocketMax?: number | null
    insuranceType: 'selfpay' | 'medical'
    insuranceCarrier?: { lookupId?: string; clinicCarrierName?: string }
  }
}

@Component({
  selector: 'mmx-shopper-insurance-card',
  templateUrl: './shopper-insurance-card.component.html',
  standalone: false,
})
export class ShopperInsuranceCardComponent implements OnChanges, OnDestroy {
  private readonly dialog = inject(MatDialog)
  private readonly apiService = inject(ApiService)
  private readonly shopperEstimateService = inject(ShopperEstimateService)
  private readonly onDestroy$ = new Subject<void>()

  @Input() shopperInsurances?: ShopperEstimate['insurances']
  @Output() insuranceChanged = new EventEmitter<ShopperInsuranceChangeEvent>()

  loading = false
  eligibility: Eligibility | null = null
  private loadedEligibilityId: string | null = null

  get primaryInsurance(): ShopperEstimateInsurance | undefined {
    return this.shopperInsurances?.P
  }

  ngOnChanges(changes: SimpleChanges) {
    const insurances = changes['shopperInsurances']?.currentValue as ShopperEstimate['insurances'] | undefined
    const eligibilityId = insurances?.P?.eligibilityId
    if (eligibilityId && eligibilityId !== this.loadedEligibilityId) {
      this.loadedEligibilityId = eligibilityId
      this.loading = true
      this.apiService.getEligibility(eligibilityId).pipe(
        take(1),
        takeUntil(this.onDestroy$),
        finalize(() => { this.loading = false }),
      ).subscribe({
        next: (eligibility) => {
          this.eligibility = eligibility
        },
        error: () => {
          this.eligibility = null
        },
      })
    }
  }

  ngOnDestroy() {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  onAddInsurance() {
    const shopperForm = this.shopperEstimateService.shopperForm.getRawValue()

    const patient = {
      name: {
        given: shopperForm.patientFirstName,
        family: shopperForm.patientLastName,
      },
      dob: shopperForm.patientDOB ? moment(shopperForm.patientDOB) : undefined,
    } as Patient

    const data: PullEligibilityDialogInput = {
      patient,
      insuranceType: PatientInsuranceType.PRIMARY,
    }

    const dialogRef = this.dialog.open(PullEligibilityDialogComponent, {
      panelClass: 'dialog-form',
      width: '500px',
      data,
    })

    dialogRef.afterClosed().pipe(
      takeUntil(this.onDestroy$),
      filter(Boolean),
      tap(() => { this.loading = true }),
      switchMap((eligibility: Eligibility) => {
        const eligibilityId = eligibility.id as string
        return this.apiService.get<EligibilityBenefits>(`/eligibilities/${eligibilityId}/benefits`).pipe(
          map(resp => ({ eligibilityId, eligibility, benefits: resp.data })),
        )
      }),
      finalize(() => { this.loading = false }),
    ).subscribe(({ eligibilityId, eligibility, benefits }) => {
      this.eligibility = eligibility
      this.loadedEligibilityId = eligibilityId
      this.fillMissingPatientData(eligibility)

      const carrier = eligibility.clinicCarrierName ?? ''
      const memberId = eligibility.data?.subscriber?.memberId ?? ''
      const lookupId = eligibility.carrierLookupId

      const primaryInsurance: ShopperEstimateInsurance = {
        carrier,
        memberId,
        eligibilityId,
        deductible: benefits?.deductible ?? 0,
        coinsurance: benefits?.coinsurance ?? 0,
        copayment: benefits?.copayment ?? 0,
        outOfPocketMax: benefits?.outOfPocketMax ?? null,
      }

      this.insuranceChanged.emit({
        insurances: {
          ...this.shopperInsurances,
          P: primaryInsurance,
        },
        eligibilityId,
        inputs: {
          deductible: primaryInsurance.deductible,
          coinsurance: primaryInsurance.coinsurance,
          copayment: primaryInsurance.copayment,
          outOfPocketMax: primaryInsurance.outOfPocketMax,
          insuranceType: 'medical',
          insuranceCarrier: {
            lookupId,
            clinicCarrierName: carrier,
          },
        },
      })
    })
  }

  onRemoveInsurance() {
    this.eligibility = null
    this.loadedEligibilityId = null
    this.insuranceChanged.emit({
      insurances: {
        ...this.shopperInsurances,
        P: undefined,
      },
      inputs: {
        deductible: 0,
        coinsurance: 0,
        copayment: 0,
        outOfPocketMax: null,
        insuranceType: 'selfpay',
        insuranceCarrier: undefined,
      },
    })
  }

  private fillMissingPatientData(eligibility: Eligibility) {
    const subscriber = eligibility.data?.subscriber
    if (!subscriber) {
      return
    }
    const form = this.shopperEstimateService.shopperForm
    if (!form.value.patientFirstName && subscriber.name?.given) {
      form.controls.patientFirstName.setValue(subscriber.name.given)
    }
    if (!form.value.patientLastName && subscriber.name?.family) {
      form.controls.patientLastName.setValue(subscriber.name.family)
    }
    if (!form.value.patientDOB && subscriber.dob) {
      form.controls.patientDOB.setValue(subscriber.dob)
    }
  }
}
